import Badge from "@/components/core/badge";
import { Button } from "@/components/core/buttons/button";
import DataTable, { type ColumnDef } from "@/components/core/data-table";
import { useModal } from "@/components/core/modals/use-modal";
import { siteDocumentationsQueryOptions, useSiteDocumentationDeleteMutation } from "@/features/dashboard/api/queries/site-documentation-query";
import type { SiteDocumentation } from "@/features/dashboard/types/site-documentation";
import { IconPencilMinus, IconPlus, IconTrash } from "@tabler/icons-react";
import { useQuery } from "@tanstack/react-query";
import { format } from "date-fns";
import { toast } from "sonner";
import SiteDocumentationModal from "./site-documentation-modal";

interface SiteDocumentationListProps {
	siteId: string;
	siteCode?: string;
	onDocumentationSelect?: (documentation: SiteDocumentation | null) => void;
	selectedDocumentationId?: number | null;
}

export function SiteDocumentationList({ siteId, siteCode, onDocumentationSelect, selectedDocumentationId }: SiteDocumentationListProps) {
	const modal = useModal();

	const deleteMutation = useSiteDocumentationDeleteMutation();

	const { data, isLoading, refetch } = useQuery({
		...siteDocumentationsQueryOptions({ site_id: siteId, per_page: 50 }),
	});

	const handleDelete = (documentation: SiteDocumentation) => {
		if (!documentation.id) return;

		modal.openModal({
			type: "confirm",
			variant: "red",
			title: "Delete Site Documentation",
			description: `Are you sure you want to delete this ${documentation.category} documentation? This action cannot be undone.`,
			onConfirm: async () => {
				try {
					await deleteMutation.mutateAsync(String(documentation.id));
					toast.success("Site documentation deleted successfully");
					refetch();
				} catch (error) {
					console.error(error);
					toast.error("Failed to delete site documentation");
				}
			},
		});
	};

	const handleEdit = (documentation: SiteDocumentation) => {
		modal.openModal({
			title: "Edit Site Documentation",
			description: "Update the documentation entry for this site",
			component: <SiteDocumentationModal data={documentation} siteId={siteId} onSuccess={() => refetch()} />,
		});
	};

	const handleCreate = () => {
		modal.openModal({
			title: "Add Site Documentation",
			description: "Create a new documentation entry for this site",
			component: <SiteDocumentationModal siteId={siteId} onSuccess={() => refetch()} />,
		});
	};

	const getCompletionBadgeVariant = (percentage: number) => {
		if (percentage >= 80) return "success";
		if (percentage >= 60) return "warning";
		return "destructive";
	};

	const columns: ColumnDef<SiteDocumentation>[] = [
		{
			id: "category",
			label: "Category",
			cell: ({ data }) => (
				<button
					className={`font-medium text-sm cursor-pointer p-2 -m-2 rounded transition-colors hover:bg-gray-50 ${
						selectedDocumentationId === data.id ? "bg-blue-50 border-l-2 border-blue-500 pl-1" : ""
					}`}
					type="button"
					onClick={() => onDocumentationSelect?.(data)}
				>
					{data.category} {data.mir_count ? ` (${data.mir_count})` : ""}
					<div>{data.documentation_date ? format(new Date(data.documentation_date), "MMM dd, yyyy") : "-"}</div>
				</button>
			),
		},
		{
			id: "completion_percentage",
			label: "Progress",
			cell: ({ data }) => {
				const uploaded = data.uploaded_checklist || 0;
				const total = data.total_checklist || 1;

				return (
					<div className="flex items-center gap-2">
						<span className="text-gray-500">
							{uploaded}/{total}
						</span>
					</div>
				);
			},
		},
		{
			id: "actions",
			label: "",
			cell: ({ data }) => (
				<div className="flex items-center gap-1">
					<Button variant="ghost" size="sm" onClick={() => handleEdit(data)} className="h-8 w-8 p-0">
						<IconPencilMinus className="h-4 w-4" />
					</Button>
					<Button variant="ghost" size="sm" onClick={() => handleDelete(data)} className="h-8 w-8 p-0 text-red-600 hover:text-red-700">
						<IconTrash className="h-4 w-4" />
					</Button>
				</div>
			),
		},
	];

	return (
		<div className="space-y-4">
			<div className="flex items-center justify-between">
				<div>
					<h3 className="text-lg font-semibold">Site Documentation</h3>
					{siteCode && <p className="text-sm text-gray-600">Documentation for {siteCode}</p>}
				</div>
				<Button variant="outline" size="sm" onClick={handleCreate} className="flex items-center gap-2">
					<IconPlus className="h-4 w-4" />
					Add Documentation
				</Button>
			</div>

			<div className="border rounded-lg">
				<DataTable
					columns={columns}
					data={data?.data || []}
					isLoading={isLoading}
					emptyState={{
						title: "No documentation found",
						description: "Start by adding documentation for this site.",
					}}
				/>
			</div>
		</div>
	);
}
